import { useIntl, type UserAgreementRequirementConfig, type UserRequirement } from '@capsicum/common';

import { useSubmitRequirementStateByIdentifierQuery } from '../data-access';

export type GenericAgreementProps = {
	isForcedRequirement?: boolean;
	onDismiss: (callback?: () => void) => void;
	requirement: UserRequirement;
};

type ConfirmAgreementProps = {
	accepted: boolean;
	close: (shouldInvokeOnDismiss?: boolean) => void;
};

type AcceptAgreementProps = {
	close: (shouldInvokeOnDismiss?: boolean) => void;
	delay: number;
	showConfirmModal: () => void;
};

type CancelAgreementProps = {
	close: (shouldInvokeOnDismiss?: boolean) => void;
	delay: number;
	showDeclineModal: () => void;
};

export const useGenericAgreement = (requirement: UserRequirement) => {
	const { getLocalizedField } = useIntl();
	const { isLoading, mutate } = useSubmitRequirementStateByIdentifierQuery();

	const config = requirement.config as Required<UserAgreementRequirementConfig>;
	const { needsConfirmation, sameButtonStyles } = config;

	const title = getLocalizedField('config[title]', requirement) || config.title;
	const subtitle = getLocalizedField('config[subtitle]', requirement) || config.subtitle;
	const agreeButtonText = getLocalizedField('config[agreeButtonText]', requirement) || config.agreeButtonText;
	const disagreeButtonText =
		getLocalizedField('config[disagreeButtonText]', requirement) || config.disagreeButtonText;

	const confirmTitle = getLocalizedField('config[confirmTitle]', requirement) || config.confirmTitle;
	const confirmSubtitle = getLocalizedField('config[confirmSubtitle]', requirement) || config.confirmSubtitle;
	const confirmAgreeButtonText =
		getLocalizedField('config[confirmAgreeButtonText]', requirement) || config.confirmAgreeButtonText;
	const confirmDisagreeButtonText =
		getLocalizedField('config[confirmDisagreeButtonText]', requirement) || config.confirmDisagreeButtonText;

	const declineTitle = getLocalizedField('config[declineTitle]', requirement) || config.declineTitle;
	const declineSubtitle = getLocalizedField('config[declineSubtitle]', requirement) || config.declineSubtitle;
	const declineAgreeButtonText =
		getLocalizedField('config[declineAgreeButtonText]', requirement) || config.declineAgreeButtonText;
	const declineDisagreeButtonText =
		getLocalizedField('config[declineDisagreeButtonText]', requirement) || config.declineDisagreeButtonText;

	const acceptAgreement = ({ close, delay, showConfirmModal }: AcceptAgreementProps) => {
		if (needsConfirmation) {
			close(false);
			setTimeout(showConfirmModal, delay);

			return;
		}

		mutate({
			requirementData: { accepted: true },
			requirementIdentifier: requirement.identifier
		});
		close(false);
	};

	const cancelAgreement = ({ close, delay, showDeclineModal }: CancelAgreementProps) => {
		if (needsConfirmation) {
			close(false);
			setTimeout(showDeclineModal, delay);
		} else {
			close();
		}
	};

	const confirmAgreement = ({ accepted, close }: ConfirmAgreementProps) => {
		mutate({
			requirementData: { accepted },
			requirementIdentifier: requirement.identifier
		});
		close(false);
	};

	return {
		acceptAgreement,
		agreeButtonText,
		cancelAgreement,
		confirmAgreeButtonText,
		confirmAgreement,
		confirmDisagreeButtonText,
		confirmSubtitle,
		confirmTitle,
		declineAgreeButtonText,
		declineDisagreeButtonText,
		declineSubtitle,
		declineTitle,
		disagreeButtonText,
		isLoading,
		sameButtonStyles,
		subtitle,
		title
	};
};
